import React from "react";
import { ActionMenu, getBreakPoint, useScreenWidth, ButtonSecondaryV2, ButtonPrimaryCompact, ButtonTertiaryCompact, TooltipV2 } from "oolib";
import { trackEventGA } from "../../../../trackers/GA/trackEventGA";
import { scrollModuleInView } from "../../../../utils/scrollModuleInView";
import { ButtonContentEngagement } from "../../../../pageTpls/PublishedPage/comps/ContentEngagementButtonPanel/Comps/ButtonContentEngagement";
import { _Locale } from "../../../locale/Locale";
import { UIContent } from "../../../../UIContent";

export const TPLActionMenu = ({
  
  /**
   * these booleans basically tell us if access control configs
   * allow for the below actions. 
   * 
   * They hide/show items from the action menu.
   */
  userHasAccessRightsToDelete, 
  userHasAccessRightsToEdit,
  modHasAccessRightsToDelete,
  modHasAccessRightsToReview,

  /**
   * these booleans basically tell us if ALL the many factors
   * are aligned to enable/disable these actions.
   * By ALL i mean several complex things such as document status,
   * is this doc currently under edit/mod etc etc
   * 
   * They enable/disable items within the action menu.
   */
  enableModButton,
  enableEditButton,
  enableDeleteButton,

  ShowShareButton,
  ShowCommentsButton,
  
  handleShareButtonClick,
  handleModeratePublished,
  handleEditPublished,

  genShareUrl,
  contentType,
  kebabStyle,
  invert,
  popOutOfOverflowHiddenParent,
  setShowDeleteModal,
  currentStatus,
  tplActionsIcon,
  buttonSecondary = false,
  // when true, render the actions as inline compact buttons (used by PublishedPageV3)
  // instead of the kebab ActionMenu.
  publishedPageV3Actions = false,
}) => {
  const screenWidth = useScreenWidth();

  const actionsConfig = [
          ...(ShowCommentsButton &&
          (screenWidth < getBreakPoint("md") || kebabStyle)
            ? [
                {
                  display: "Comments",
                  icon: "ChatText",
                  onClick: (e) => {
                    scrollModuleInView("commentsModule");
                  },
                },
              ]
            : []),
          ...(ShowShareButton &&
          (screenWidth < getBreakPoint("md") || kebabStyle)
            ? [
                {
                  display: genShareUrl.isLoading ? "Creating Url..." : "Share",
                  icon: "ShareNetwork",
                  onClick: () => handleShareButtonClick(),
                },
              ]
            : []),

          userHasAccessRightsToEdit
            ? {
                display: "Edit",
                icon: "PencilSimple",
                onClick: () =>
                  enableEditButton ? handleEditPublished() : () => {},
                disabled: !enableEditButton,
                infoTooltip: enableEditButton
                  ? undefined
                  : {
                      text: `${_Locale(
                        UIContent.disableEditInfoTooltip[currentStatus]
                      )}`,
                    },
              }
            : undefined,

          modHasAccessRightsToReview
            ? {
                display: "Review",
                icon: "UserCirclePlus",
                disabled: !enableModButton,
                onClick: () =>
                  enableModButton ? handleModeratePublished() : () => {},
                infoTooltip: enableModButton
                  ? undefined
                  : {
                      text: `${_Locale(
                        UIContent.disableReviewInfoTooltip[currentStatus]
                      )}`,
                    },
              }
            : undefined,

          modHasAccessRightsToDelete
            ? {
                display: "Delete",
                icon: "Trash",
                disabled: !enableDeleteButton,
                onClick: enableDeleteButton
                  ? (e) => {
                      trackEventGA(
                        "Published Content",
                        `Clicked Delete On Published Content`,
                        `${contentType}`
                      );
                      setShowDeleteModal(true);
                    }
                  : () => {},
                infoTooltip: enableDeleteButton
                  ? undefined
                  : {
                      text: `${_Locale(
                        UIContent.disableDeleteForModInfoTooltip
                      )}`, // editPublished, underModeration, sentBack
                    },
              }
            : undefined,

          userHasAccessRightsToDelete
            ? {
                display: "Delete",
                icon: "Trash",
                disabled: !enableDeleteButton,
                onClick: (e) => {
                  trackEventGA(
                    "Published Content",
                    `Clicked Delete On Published Content`,
                    `${contentType}`
                  );
                  setShowDeleteModal(true);
                },
                infoTooltip: enableDeleteButton
                  ? undefined
                  : {
                      text: `${_Locale(
                        UIContent.disableDeleteInfoTooltip[currentStatus]
                      )}`, // editPublished, underModeration, sentBack
                    },
              }
            : undefined,
          ,
        ].filter(d=>d)

  if(actionsConfig.length === 0) return null;

  // compact-buttons style: render each action as an inline compact button instead
  // of the kebab dropdown. Delete uses the tertiary (subtle) style, edit/review
  // the primary style — at most these two show, matching the V3 header design.
  if (publishedPageV3Actions) {
    // V3 header design wants Delete shown before Edit/Review, so float the
    // delete (Trash) action(s) to the front without touching the canonical
    // actionsConfig order used by the kebab menu.
    const orderedActions = [
      ...actionsConfig.filter((action) => action.icon === "Trash"),
      ...actionsConfig.filter((action) => action.icon !== "Trash"),
    ];
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
        {orderedActions.map((action, i) => {
          const Btn = action.icon === "Trash" ? ButtonTertiaryCompact : ButtonPrimaryCompact;
          const btn = (
            <Btn
              icon={action.icon}
              value={action.display}
              disabled={action.disabled}
              onClick={action.onClick}
            />
          );
          // disabled buttons swallow hover, so anchor the tooltip on a wrapping span
          return action.disabled && action.infoTooltip ? (
            <TooltipV2 key={i} text={action.infoTooltip.text} placement="bottom" showPointer>
              <span style={{ display: "inline-flex" }}>{btn}</span>
            </TooltipV2>
          ) : (
            <React.Fragment key={i}>{btn}</React.Fragment>
          );
        })}
      </div>
    );
  }

  return (
    <div className="kebabMenu">
      <ActionMenu
        invert={invert}
        align="right"
        iconSize="20"
        CustomSelectComp={({ active, onClick }) => {
          return (
            buttonSecondary 
            ? 
            <ButtonSecondaryV2 icon="DotsThree" onClick={onClick} active={active}  /> 
            : 
            <ButtonContentEngagement
              invert={invert}
              icon={tplActionsIcon || "DotsThree"}
              onClick={onClick}
              active={active}
            />
          );
        }}
        popOutOfOverflowHiddenParent={popOutOfOverflowHiddenParent}
        actions={actionsConfig}
      />
    </div>
  );
};
